/**
 * Records (04 §5): what `report` notes down per archetype and per ranger, and the table the
 * Records screen reads. Everything shown is derived from the profile; nothing is stored twice.
 * Operation and Classic missions count. Practice and the Daily Drop do not, nor does a scrub.
 */

import type { ArchetypeId } from '../sim/types';
import { type ArchetypeStats, fullName, type ProfileState, snapshot } from './state';
import type { ArchetypeRecord, RangerRecord, Records, ServiceEntry } from './types';

/** How many missions one ranger's file keeps. A career is shorter than this; a Classic roster is not. */
export const FILE_DEPTH = 40;

export interface Flight {
  archetype: ArchetypeId;
  rangerId: string;
  /** Extracted with the objective done. */
  success: boolean;
  secondsUsed: number | undefined;
  /** The row for his service file. Absent only where a caller has nothing to file (a test). */
  entry?: ServiceEntry;
}

/**
 * Note one flown mission: the archetype's attempts, completions and best time; the ranger's run of
 * successes; and the row in his own service file. The file is the only per-mission history the game
 * keeps, so this is the one place it can be written - both callers of this function are the two
 * places a mission is ever reported (`reportOperation`, `reportClassic`).
 */
export function recordFlight(profile: ProfileState, flight: Flight): void {
  if (flight.entry !== undefined) {
    const file = profile.flights[flight.rangerId] ?? [];
    file.push(flight.entry);
    // Oldest first, and the oldest fall off the front: a long career keeps what he did lately.
    profile.flights[flight.rangerId] = file.slice(-FILE_DEPTH);
  }
  const stats: ArchetypeStats = profile.archetypeStats[flight.archetype]
    ?? { attempts: 0, completions: 0, bestSeconds: null };
  stats.attempts += 1;
  if (flight.success) {
    stats.completions += 1;
    const seconds = flight.secondsUsed;
    if (seconds !== undefined && Number.isFinite(seconds) && seconds > 0) {
      stats.bestSeconds = stats.bestSeconds === null ? seconds : Math.min(stats.bestSeconds, seconds);
    }
  }
  profile.archetypeStats[flight.archetype] = stats;

  // A streak is one ranger's: anything short of a success ends it, and the dead keep no entry.
  if (flight.success) {
    const streak = (profile.streaks[flight.rangerId] ?? 0) + 1;
    profile.streaks[flight.rangerId] = streak;
    profile.bestStreak = Math.max(profile.bestStreak, streak);
  } else delete profile.streaks[flight.rangerId];
}

/** Everyone the profile has ever known: company, Hall, roll of honour, Classic roster, and the wall. */
export function everyRanger(profile: ProfileState): RangerRecord[] {
  return [
    ...(profile.operation?.company ?? []), ...profile.hall, ...profile.retired, ...profile.classicRoster,
    ...profile.memorial.map((entry) => entry.ranger),
  ];
}

/**
 * Drop the files of men the profile no longer knows. Nearly everyone ends up somewhere permanent -
 * the Hall, the roll of honour, the wall - but a survivor of a won operation who is never inducted
 * is simply dropped when the next operation starts, and his file would otherwise sit there for good.
 */
export function pruneFlights(profile: ProfileState): void {
  const known = new Set(everyRanger(profile).map((r) => r.id));
  for (const id of Object.keys(profile.flights)) if (!known.has(id)) delete profile.flights[id];
}

/** The first ranger with the highest `measure`, or null while nobody has any. */
function leader(rangers: readonly RangerRecord[], measure: (r: RangerRecord) => number): RangerRecord | null {
  let best: RangerRecord | null = null;
  for (const r of rangers) if (measure(r) > (best ? measure(best) : 0)) best = r;
  return best;
}

function byArchetype(profile: ProfileState): Records['byArchetype'] {
  const table: Partial<Record<ArchetypeId, ArchetypeRecord>> = {};
  const ids = new Set([...Object.keys(profile.archetypeStats), ...Object.keys(profile.bestScores)]) as Set<ArchetypeId>;
  for (const id of ids) {
    const stats = profile.archetypeStats[id];
    const bestScore = profile.bestScores[id] ?? 0;
    // A best score from before attempts were counted still proves one completion.
    const completions = Math.max(stats?.completions ?? 0, bestScore > 0 ? 1 : 0);
    table[id] = {
      attempts: Math.max(stats?.attempts ?? 0, completions), completions, bestScore,
      bestSeconds: stats?.bestSeconds ?? null,
    };
  }
  return table;
}

/** The Records screen's table, built fresh from the profile on every call. */
export function deriveRecords(profile: ProfileState): Records {
  const rangers = everyRanger(profile);
  const served = leader(rangers, (r) => r.record.missions);
  const decorated = leader(rangers, (r) => r.medals.length);
  return {
    byArchetype: byArchetype(profile),
    longestServing: served ? { name: fullName(served), missions: served.record.missions } : null,
    mostDecorated: decorated ? { name: fullName(decorated), medals: decorated.medals.length } : null,
    bestStreak: profile.bestStreak,
    operationsWon: profile.operationsWon,
    rangersLost: profile.memorial.length,
    daily: snapshot(profile.daily),
  };
}
